import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import Admin from './Admin';
import Imports from './Imports';

// Showcase slides defined outside component to prevent recreation on each render
const SHOWCASE_SLIDES = [
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/lab3bg9t_FB_IMG_1769530008147.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/5g27skej_FB_IMG_1769530073628.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/0yadwd2s_FB_IMG_1769530107783.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/mfyanaz8_FB_IMG_1769530123695.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/yp2thbfx_FB_IMG_1769530157833.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/jj6lj0qw_FB_IMG_1769530174268.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/lra04rdk_FB_IMG_1769529468053.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/9c7w3msd_FB_IMG_1769529544812.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/vf232vgg_FB_IMG_1769529596363.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/8o53zuh3_FB_IMG_1769529899836.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/xx6jyf1l_FB_IMG_1769529875039.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/hs8avbte_FB_IMG_1769531194183.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/co9vns75_FB_IMG_1769531017941.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/ccigw25l_FB_IMG_1769530902306.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/tlekcvgi_FB_IMG_1769530800152.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/fb8h583f_FB_IMG_1769530504746.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/uqjeyn0u_FB_IMG_1769531265453.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/r2281pcn_FB_IMG_1769531250900.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/0l8jvdco_FB_IMG_1769530399327.jpg',
  'https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/2mra9cn9_FB_IMG_1769530237610.jpg'
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/*" element={<Admin />} />
        <Route path="/imports" element={<Imports />} />
        <Route path="/" element={<MainWebsite />} />
      </Routes>
    </BrowserRouter>
  );
}

function MainWebsite() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);
  const [activeVehicleImageIndex, setActiveVehicleImageIndex] = useState(0);
  const [previewVehicle, setPreviewVehicle] = useState(null);
  const [reservationForm, setReservationForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    down_payment: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle_interest: '',
    message: ''
  });

  // New state for search/filter
  const [searchFilters, setSearchFilters] = useState({
    make: '',
    minPrice: '',
    maxPrice: '',
    year: '',
    status: ''
  });

  // New state for financing calculator
  const [calcPrice, setCalcPrice] = useState(12000);
  const [calcDownPayment, setCalcDownPayment] = useState(35);
  const [calcMonths, setCalcMonths] = useState(24);
  const financeApr = 24;

  // FAQ expanded state
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Slideshow state and effect - using constant defined outside component
  const [showcaseIndex, setShowcaseIndex] = useState(0);
  const [isSlideTransitioning, setIsSlideTransitioning] = useState(false);

  useEffect(() => {
    const slideInterval = setInterval(() => {
      setIsSlideTransitioning(true);
      setTimeout(() => {
        setShowcaseIndex((prevIndex) => (prevIndex + 1) % SHOWCASE_SLIDES.length);
        setIsSlideTransitioning(false);
      }, 300); // Fade out duration
    }, 4000); // Change slide every 4 seconds
    
    return () => clearInterval(slideInterval);
  }, []); // Empty dependency array - runs once on mount

  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingSummary, setRatingSummary] = useState({ average: 0, total: 0, counts: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 } });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, customersRes, ratingSummaryRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/sold-vehicles`),
        axios.get(`${API}/ratings/summary`)
      ]);
      setVehicles(vehiclesRes.data);
      setCustomers(customersRes.data);
      setRatingSummary(ratingSummaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const handleReservation = async (e) => {
    e.preventDefault();
    if (!selectedVehicle) return;

    try {
      await axios.post(`${API}/reserve`, {
        vehicle_id: selectedVehicle.id,
        ...reservationForm
      });
      alert('Reservation submitted successfully! We will contact you shortly.');
      setSelectedVehicle(null);
      setReservationForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        down_payment: ''
      });
      fetchData(); // Refresh data
    } catch (error) {
      alert('Error submitting reservation. Please try again.');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, contactForm);
      alert('Message sent successfully! We will get back to you soon.');
      setContactForm({
        name: '',
        email: '',
        phone: '',
        vehicle_interest: '',
        message: ''
      });
    } catch (error) {
      alert('Error sending message. Please try again.');
    }
  };

  const submitRating = async () => {
    if (!ratingStars) {
      alert('Please select a star rating first.');
      return;
    }
    try {
      await axios.post(`${API}/ratings`, { stars: ratingStars, comment: ratingComment || null });
      setRatingSubmitted(true);
      setRatingStars(0);
      setRatingComment('');
      fetchData();
    } catch (e) {
      alert('Error submitting rating. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'AVAILABLE': { bg: 'bg-green-600', text: 'Available Now' },
      'PENDING ARRIVAL': { bg: 'bg-yellow-600', text: 'Pending Arrival' },
      'RESERVED': { bg: 'bg-orange-600', text: 'Reserved' },
      'SOLD': { bg: 'bg-red-600', text: 'SOLD' }
    };
    return badges[status] || { bg: 'bg-gray-600', text: status };
  };

  const canReserve = (status) => {
    return ['AVAILABLE', 'PENDING ARRIVAL'].includes(status);
  };


  // Sort customers by most recent first (newest at top)
  // Priority: sold_year descending, then created_at descending
  const displayCustomers = [...customers].sort((a, b) => {
    // First compare by sold_year (most recent first)
    const yearA = a.sold_year || 0;
    const yearB = b.sold_year || 0;
    if (yearB !== yearA) return yearB - yearA;
    
    // Then by created_at if available
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });

  const getCountryFlag = (country) => {
    const flags = {
      'Japan': '🇯🇵',
      'UK': '🇬🇧',
      'USA': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'United States': '🇺🇸'
    };
    return flags[country] || '🌍';
  };

  const renderStars = (rating = 5) => {
    return Array.from({ length: rating }, (_, i) => (
      <span key={i} className="text-red-500 text-lg">★</span>
    ));
  };

  const formatCustomerDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Show ALL vehicles in inventory, with AVAILABLE vehicles first (newest at top)
  // Priority: AVAILABLE > PENDING ARRIVAL > RESERVED > ORDER NOW > SOLD
  const statusOrder = {
    'AVAILABLE': 1,
    'PENDING ARRIVAL': 2,
    'RESERVED': 3,
    'ORDER NOW': 4,
    'SOLD': 5
  };
  
  const getPinnedPriority = (vehicle) => {
    const primaryImage = vehicle.images?.[0];

    if (
      vehicle.make === 'Toyota' &&
      vehicle.model === 'C-HR Hybrid' &&
      vehicle.year === 2019 &&
      vehicle.status === 'PENDING ARRIVAL' &&
      primaryImage === 'https://customer-assets.emergentagent.com/job_491874df-ad29-410a-94ab-bd11e54c3ec3/artifacts/xfc4pmu4_1000854834.jpg'
    ) {
      return 0;
    }

    if (
      vehicle.make === 'Suzuki' &&
      vehicle.model === 'Swift Hybrid MG' &&
      vehicle.year === 2020 &&
      vehicle.status === 'ORDER NOW' &&
      primaryImage === 'https://customer-assets.emergentagent.com/job_491874df-ad29-410a-94ab-bd11e54c3ec3/artifacts/fxmqlzg3_1000715968.jpg'
    ) {
      return 1;
    }

    if (
      vehicle.make === 'Toyota' &&
      vehicle.model === 'C-HR' &&
      vehicle.year === 2021 &&
      vehicle.status === 'ORDER NOW' &&
      primaryImage === 'https://customer-assets.emergentagent.com/job_491874df-ad29-410a-94ab-bd11e54c3ec3/artifacts/xvhnyef8_1000835530.jpg'
    ) {
      return 2;
    }

    return 3;
  };

  const sortedVehicles = [...vehicles].sort((a, b) => {
    const pinnedPriorityA = getPinnedPriority(a);
    const pinnedPriorityB = getPinnedPriority(b);
    if (pinnedPriorityA !== pinnedPriorityB) return pinnedPriorityA - pinnedPriorityB;

    // First sort by status priority
    const statusA = statusOrder[a.status] || 99;
    const statusB = statusOrder[b.status] || 99;
    if (statusA !== statusB) return statusA - statusB;
    
    // Then by created_at (newest first)
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
  
  const availableVehicles = sortedVehicles;
  const hotDeals = availableVehicles.filter(v => v.status === 'AVAILABLE').slice(0, 3);
  const soldVehicles = availableVehicles.filter(v => v.status === 'SOLD');

  // Filter vehicles based on search criteria
  const filteredVehicles = availableVehicles.filter(v => {
    if (searchFilters.make && !`${v.make} ${v.model}`.toLowerCase().includes(searchFilters.make.toLowerCase())) return false;
    if (searchFilters.year && v.year !== parseInt(searchFilters.year)) return false;
    if (searchFilters.status && v.status !== searchFilters.status) return false;
    if (searchFilters.minPrice || searchFilters.maxPrice) {
      const priceMatch = v.price.match(/\d+,?\d*/);
      if (priceMatch) {
        const price = parseInt(priceMatch[0].replace(',', ''));
        if (searchFilters.minPrice && price < parseInt(searchFilters.minPrice)) return false;
        if (searchFilters.maxPrice && price > parseInt(searchFilters.maxPrice)) return false;
      }
    }
    return true;
  });

  // Get unique makes for filter dropdown
  const uniqueMakes = [...new Set(vehicles.map(v => v.make))];
  const uniqueYears = [...new Set(vehicles.map(v => v.year))].sort((a, b) => b - a);

  // Calculate monthly payment with financing APR
  const calculateFinanceQuote = () => {
    const downPaymentAmount = Math.round(calcPrice * (calcDownPayment / 100));
    const principal = calcPrice - downPaymentAmount;
    const monthlyRate = financeApr / 100 / 12;
    const exactMonthlyPayment = principal > 0
      ? (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -calcMonths))
      : 0;
    const monthlyPayment = Math.ceil(exactMonthlyPayment);
    const totalWithFinancing = downPaymentAmount + (monthlyPayment * calcMonths);
    const financeCharge = totalWithFinancing - calcPrice;

    return {
      downPaymentAmount,
      principal,
      monthlyPayment,
      totalWithFinancing,
      financeCharge,
    };
  };

  const financeQuote = calculateFinanceQuote();

  // FAQ data
  const faqData = [
    {
      question: "How does the import process work?",
      answer: "We source quality vehicles from Japan, UK, and USA. You pay 50% down payment to reserve your vehicle, and we handle all shipping, customs, and documentation. The remaining 50% is due upon arrival. Typical delivery time is 6-8 weeks."
    },
    {
      question: "What warranty do you offer?",
      answer: "All vehicles come with a 3-month warranty covering major mechanical components. We also provide 1 year of free inspections. Please note: warranty does not cover electronic parts or normal wear and tear."
    },
    {
      question: "How does financing work?",
      answer: "We offer flexible financing plans with competitive rates. Down payment starts from KYD $5,500 depending on the vehicle. Monthly payments range from KYD $350-$750. You'll need proof of stable employment or business income to qualify."
    },
    {
      question: "Can I see the vehicle before buying?",
      answer: "For vehicles in our local inventory, yes! Visit our showroom at 110 North Sound Road. For imports, we provide detailed photos, inspection reports, and vehicle history from Japan/UK/USA auctions."
    },
    {
      question: "What documents do I need for financing?",
      answer: "For employed individuals: Employment letter, valid ID, and work permit (if applicable). For self-employed: Business financial statements for the past year and valid ID."
    },
    {
      question: "Do you ship to other islands?",
      answer: "Yes! We sell vehicles locally and internationally including Jamaica, Dominican Republic, Trinidad, and other Caribbean islands. Contact us for shipping quotes."
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-pulse text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App bg-gray-900">
      {/* Navigation - Mobile Optimized */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-red-900/30 shadow-lg shadow-black/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_car-showcase-105/artifacts/805j4p0n_Gemini%20Car%20Imports%20%28Logo%29%202%202021.png" 
                alt="Gemini Cars Import Logo" 
                className="h-9 w-9 md:h-10 md:w-10 rounded-full ring-2 ring-red-600/50"
              />
              <div className="ml-2 md:ml-3">
                <h1 className="text-white text-base md:text-xl font-bold tracking-tight">Gemini Cars</h1>
                <p className="text-red-500 text-[10px] md:text-xs font-medium">Premium Imports</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:block">
              <div className="ml-10 flex items-center space-x-1">
                <button onClick={() => scrollToSection('home')} className="text-gray-300 hover:text-white hover:bg-white/5 px-3 py-2 rounded-lg text-sm font-medium transition-all">Home</button>
                <button onClick={() => scrollToSection('inventory')} className="text-gray-300 hover:text-white hover:bg-white/5 px-3 py-2 rounded-lg text-sm font-medium transition-all">Inventory</button>
                <button onClick={() => scrollToSection('financing')} className="text-gray-300 hover:text-white hover:bg-white/5 px-3 py-2 rounded-lg text-sm font-medium transition-all">Financing</button>
                <button onClick={() => scrollToSection('testimonials')} className="text-gray-300 hover:text-white hover:bg-white/5 px-3 py-2 rounded-lg text-sm font-medium transition-all">Reviews</button>
                <a href="https://wa.me/13459236920" target="_blank" rel="noopener noreferrer" className="ml-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-lg shadow-red-900/30 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
              </div>
            </div>
            
            {/* Mobile - Quick Actions + Menu */}
            <div className="flex items-center gap-2 lg:hidden">
              <a href="https://wa.me/13459236920" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-500 text-white p-2 rounded-lg transition-all">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a href="tel:+13459306988" className="bg-red-600 hover:bg-red-500 text-white p-2 rounded-lg transition-all">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </a>
              <button onClick={() => setMenuOpen(!menuOpen)} className="text-white bg-white/10 hover:bg-white/20 p-2 rounded-lg transition-all">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {menuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          
          {/* Mobile Navigation Menu - Slide Down */}
          <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="py-3 space-y-1 border-t border-red-900/30">
              <button onClick={() => { scrollToSection('home'); setMenuOpen(false); }} className="text-gray-200 hover:text-white hover:bg-red-600/20 block px-4 py-3 text-base font-medium w-full text-left rounded-lg transition-all">
                <span className="flex items-center gap-3"><svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg> Home</span>
              </button>
              <button onClick={() => { scrollToSection('inventory'); setMenuOpen(false); }} className="text-gray-200 hover:text-white hover:bg-red-600/20 block px-4 py-3 text-base font-medium w-full text-left rounded-lg transition-all">
                <span className="flex items-center gap-3"><svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg> Browse Inventory</span>
              </button>
              <button onClick={() => { scrollToSection('financing'); setMenuOpen(false); }} className="text-gray-200 hover:text-white hover:bg-red-600/20 block px-4 py-3 text-base font-medium w-full text-left rounded-lg transition-all">
                <span className="flex items-center gap-3"><svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Financing</span>
              </button>
              <button onClick={() => { scrollToSection('testimonials'); setMenuOpen(false); }} className="text-gray-200 hover:text-white hover:bg-red-600/20 block px-4 py-3 text-base font-medium w-full text-left rounded-lg transition-all">
                <span className="flex items-center gap-3"><svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg> Happy Customers</span>
              </button>
              <button onClick={() => { scrollToSection('contact'); setMenuOpen(false); }} className="text-gray-200 hover:text-white hover:bg-red-600/20 block px-4 py-3 text-base font-medium w-full text-left rounded-lg transition-all">
                <span className="flex items-center gap-3"><svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> Contact Us</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Mobile Optimized */}
      <section id="home" className="min-h-[72vh] md:min-h-[82vh] bg-black flex items-center relative overflow-hidden pt-14">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 via-black to-black z-10"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1692406069831-0bb7ea297645')] bg-cover bg-center opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 text-center py-8 md:py-0">
          {/* Trust Badge */}
          <div className="mb-4 md:mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-full text-xs md:text-sm font-medium border border-red-600/40 backdrop-blur-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              Trusted Since 2014
            </span>
          </div>

          {/* Customer Showcase - Compact on Mobile */}
          <div className="max-w-4xl mx-auto mb-6 md:mb-10">
            <div className="flex justify-center mb-2">
              <div className="relative inline-flex items-center gap-2 bg-gradient-to-r from-red-700 to-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg border border-red-500/30">
                <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                  <span className="text-red-600 font-black text-[10px]">G</span>
                </div>
                <span className="uppercase tracking-wide text-[10px] md:text-xs">Imported by Gemini</span>
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              </div>
            </div>

            <div className="relative h-[140px] md:h-[180px] rounded-xl overflow-hidden border border-red-600/30 bg-black/50 shadow-2xl shadow-red-900/20">
              <img
                key={showcaseIndex}
                src={SHOWCASE_SLIDES[showcaseIndex]}
                alt={`Happy customer ${showcaseIndex + 1}`}
                className={`w-full h-full object-contain transition-opacity duration-500 ${isSlideTransitioning ? 'opacity-0' : 'opacity-100'}`}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/70 pointer-events-none" />
              
              {/* Slide indicators */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {[0,1,2,3,4,5].map((idx) => (
                  <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${idx === (showcaseIndex % 6) ? 'bg-red-500 w-4' : 'bg-gray-500/60'}`} />
                ))}
              </div>
              
              {/* Counter */}
              <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm">
                {showcaseIndex + 1}/{SHOWCASE_SLIDES.length}
              </div>
            </div>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight tracking-tight">
            Premium Cars<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600">Imported</span> For You
          </h1>
          
          {/* Subtext */}
          <p className="text-base md:text-xl text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed">
            Quality vehicles from <span className="text-white font-semibold">Japan, UK & USA</span>. 
            Serving Grand Cayman, Jamaica, Trinidad & the Caribbean.
          </p>
          
          {/* CTA Buttons - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <button onClick={() => scrollToSection('inventory')} className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white px-6 py-3.5 rounded-xl text-base font-bold transition-all transform hover:scale-105 shadow-lg shadow-red-900/40 flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
              Browse Inventory
            </button>
            <a href="https://wa.me/13459236920" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-500 text-white px-6 py-3.5 rounded-xl text-base font-bold transition-all flex items-center justify-center gap-2 shadow-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Message on WhatsApp
            </a>
          </div>
          
          {/* Stats Cards - Mobile Optimized */}
          <div className="hidden sm:grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-red-900/30">
              <div className="text-2xl md:text-3xl font-black text-red-500">500+</div>
              <div className="text-gray-400 text-xs md:text-sm">Cars Imported</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-red-900/30">
              <div className="text-2xl md:text-3xl font-black text-red-500">120+</div>
              <div className="text-gray-400 text-xs md:text-sm">Happy Customers</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-red-900/30">
              <div className="text-2xl md:text-3xl font-black text-red-500">10+</div>
              <div className="text-gray-400 text-xs md:text-sm">Years Experience</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-red-900/30">
              <div className="text-2xl md:text-3xl font-black text-red-500">3</div>
              <div className="text-gray-400 text-xs md:text-sm">Source Countries</div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Now / Complete Inventory Section - MOVED UP */}
      <section id="inventory" className="py-12 md:py-20 bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-block px-4 py-1 bg-green-600 text-white rounded-md text-sm font-semibold mb-4">
              Available Now
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Vehicles In Stock</h2>
            <p className="text-lg md:text-xl text-gray-300">Ready for immediate purchase - Drive away today!</p>
          </div>

          {/* Search & Filter Bar */}
          <div className="bg-gray-900/80 border border-red-700/30 rounded-2xl p-4 md:p-6 mb-8">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-gray-400 text-xs mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Make or model..."
                  value={searchFilters.make}
                  onChange={(e) => setSearchFilters({...searchFilters, make: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Year</label>
                <select
                  value={searchFilters.year}
                  onChange={(e) => setSearchFilters({...searchFilters, year: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                >
                  <option value="">All Years</option>
                  {uniqueYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Status</label>
                <select
                  value={searchFilters.status}
                  onChange={(e) => setSearchFilters({...searchFilters, status: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                >
                  <option value="">All Status</option>
                  <option value="AVAILABLE">Available</option>
                  <option value="PENDING ARRIVAL">Pending Arrival</option>
                  <option value="RESERVED">Reserved</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Max Price</label>
                <select
                  value={searchFilters.maxPrice}
                  onChange={(e) => setSearchFilters({...searchFilters, maxPrice: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                >
                  <option value="">Any Price</option>
                  <option value="10000">Under $10,000</option>
                  <option value="15000">Under $15,000</option>
                  <option value="20000">Under $20,000</option>
                  <option value="30000">Under $30,000</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="text-gray-400 text-sm">
                Showing <span className="text-red-400 font-bold">{filteredVehicles.filter(v => v.status !== 'ORDER NOW' && v.status !== 'SOLD').length}</span> of {availableVehicles.filter(v => v.status !== 'ORDER NOW' && v.status !== 'SOLD').length} vehicles
              </div>
              <button
                onClick={() => setSearchFilters({ make: '', minPrice: '', maxPrice: '', year: '', status: '' })}
                className="text-red-400 hover:text-red-300 text-sm font-medium"
              >
                Clear Filters
              </button>
            </div>
          </div>
          
          <div className="space-y-6 md:space-y-8">
            {filteredVehicles
              .filter(v => v.status !== 'ORDER NOW' && v.status !== 'SOLD')
              .map((vehicle, index) => {
              const statusBadge = getStatusBadge(vehicle.status);
              const isHot = index < 3;
              const isLuxury = ['Mercedes-Benz', 'BMW', 'Audi', 'Lexus', 'Porsche'].includes(vehicle.make);
              const isSport = vehicle.model?.toLowerCase().includes('type r') || vehicle.model?.toLowerCase().includes('sport') || vehicle.model?.toLowerCase().includes('gt');
              
              return (
                <div
                  key={vehicle.id}
                  className={`rounded-xl overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${
                    isLuxury 
                      ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 ring-1 ring-yellow-500/30' 
                      : isSport 
                        ? 'bg-gradient-to-br from-gray-900 via-red-950/20 to-gray-900 ring-1 ring-red-500/30'
                        : 'bg-gray-900'
                  }`}
                  onMouseEnter={() => setPreviewVehicle(vehicle)}
                  onMouseLeave={() => setPreviewVehicle(null)}
                >
                  <div className="relative">
                    <img 
                      src={vehicle.images[0] || 'https://via.placeholder.com/600x300'} 
                      alt={`${vehicle.make} ${vehicle.model}`} 
                      className="w-full h-64 md:h-80 object-contain object-center bg-gradient-to-b from-gray-800 to-gray-900" 
                    />
                    
                    <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded text-xs md:text-sm flex items-center gap-1">
                      <span>{vehicle.from_country === 'Japan' ? '🇯🇵' : vehicle.from_country === 'UK' ? '🇬🇧' : vehicle.from_country === 'USA' ? '🇺🇸' : '🌍'}</span>
                      {vehicle.from_country || 'Japan'}
                    </div>
                    
                    {isLuxury && (
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        ✨ LUXURY
                      </div>
                    )}
                    
                    {isSport && !isLuxury && (
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-red-600 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        🏎️ SPORT
                      </div>
                    )}
                    
                    <div className={`absolute bottom-3 left-3 ${statusBadge.bg} text-white px-3 py-2 rounded-lg text-sm md:text-base font-bold shadow-lg`}>
                      {statusBadge.text}
                    </div>
                    
                    {isHot && (
                      <div className="absolute top-3 right-3 bg-gradient-to-r from-red-600 to-red-700 text-white px-2 py-1 rounded-lg shadow-lg text-xs md:text-sm font-bold border border-red-400 animate-pulse">
                        🔥 HOT
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 md:p-6">
                    <h3 className={`text-xl md:text-2xl font-bold mb-3 ${isLuxury ? 'text-yellow-400' : 'text-white'}`}>
                      {vehicle.make} {vehicle.model} {vehicle.year}
                    </h3>
                    
                    <p className="text-gray-300 mb-4 text-sm md:text-base leading-relaxed line-clamp-2">
                      {vehicle.description || `${vehicle.fuel || 'Hybrid'}, ${vehicle.engine || '1500cc'} engine. Features: Power Steering, Power Windows, Back Camera, Navigation...`}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6">
                      <div className="bg-black/30 rounded-lg p-2">
                        <div className="text-gray-400 text-xs">Year</div>
                        <div className="text-white font-bold text-base md:text-lg">{vehicle.year}</div>
                      </div>
                      <div className="bg-black/30 rounded-lg p-2">
                        <div className="text-gray-400 text-xs">Mileage</div>
                        <div className="text-white font-bold text-base md:text-lg">{vehicle.mileage || '30,144 km'}</div>
                      </div>
                      <div className="bg-black/30 rounded-lg p-2">
                        <div className="text-gray-400 text-xs">Transmission</div>
                        <div className="text-white font-bold text-base md:text-lg">{vehicle.transmission || 'Automatic'}</div>
                      </div>
                      <div className="bg-black/30 rounded-lg p-2">
                        <div className="text-gray-400 text-xs">From</div>
                        <div className="text-white font-bold text-base md:text-lg">{vehicle.from_country || 'Japan'}</div>
                      </div>
                    </div>
                    
                    <div className="mb-6 bg-gradient-to-r from-red-900/30 to-transparent rounded-lg p-3">
                      {vehicle.original_price && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-gray-500 line-through text-lg">{vehicle.original_price}</span>
                          <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">SALE!</span>
                        </div>
                      )}
                      <div className={`text-2xl md:text-3xl font-black mb-1 ${isLuxury ? 'text-yellow-400' : vehicle.original_price ? 'text-green-400' : 'text-red-400'}`}>{vehicle.price}</div>
                      <div className="text-gray-300 text-sm">50% Down: {
                        (() => {
                          const priceMatch = vehicle.price.match(/\d+,?\d*/);
                          if (priceMatch) {
                            const price = parseInt(priceMatch[0].replace(',', ''));
                            const downPayment = Math.floor(price / 2);
                            return `KYD $${downPayment.toLocaleString()}`;
                          }
                          return 'Contact for Details';
                        })()
                      }</div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <button 
                        onClick={() => {
                          setActiveVehicleImageIndex(0);
                          setSelectedVehicleDetails(vehicle);
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-3 px-4 md:px-6 rounded-lg font-semibold transition-all hover:shadow-lg text-sm md:text-base"
                      >
                        View Details
                      </button>
                      
                      {canReserve(vehicle.status) ? (
                        <button 
                          onClick={() => setSelectedVehicle(vehicle)}
                          className="flex-1 bg-green-600 hover:bg-green-500 text-white py-3 px-4 md:px-6 rounded-lg font-semibold transition-all hover:shadow-lg text-sm md:text-base"
                        >
                          Reserve Now
                        </button>
                      ) : vehicle.status === 'SOLD' ? (
                        <div className="flex-1 bg-gray-700 text-gray-400 py-3 px-4 md:px-6 rounded-lg text-center font-semibold text-sm md:text-base">
                          SOLD
                        </div>
                      ) : (
                        <button 
                          onClick={() => scrollToSection('contact')}
                          className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-3 px-4 md:px-6 rounded-lg font-semibold transition-all hover:shadow-lg text-sm md:text-base"
                        >
                          Contact for Details
                        </button>
                      )}
                      
                      <button 
                        onClick={() => scrollToSection('contact')}
                        className="flex-1 border-2 border-red-600 text-red-400 hover:bg-red-600 hover:text-white py-3 px-4 md:px-6 rounded-lg font-semibold transition-colors text-sm md:text-base"
                      >
                        Inquire
                      </button>
                    </div>

                    <div className="mt-4 text-center text-sm md:text-base font-semibold text-green-300">
                      ✅ 3 Month Warranty • ✅ 1 Year Free Inspection
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Order Now Section - MOVED DOWN */}
      <section id="order-now" className="py-12 md:py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Order Now</h2>
            <p className="text-red-200">Arrival ETA 6–8 Weeks • Secure your vehicle early</p>
          </div>

          <div className="space-y-6">
            {availableVehicles
              .filter(v => v.status === 'ORDER NOW')
              .map((vehicle) => (
                <div key={vehicle.id} className="bg-gray-900/60 border border-red-700/40 rounded-2xl overflow-hidden">
                  <div className="relative">
                    <button
                      onClick={() => {
                        setActiveVehicleImageIndex(0);
                        setSelectedVehicleDetails(vehicle);
                      }}
                      className="block w-full text-left"
                    >
                      <div className="relative h-64 overflow-hidden bg-black/30">
                        {vehicle.make === 'Toyota' && vehicle.model === 'C-HR' && vehicle.year === 2021 ? (
                          <>
                            <img
                              src={vehicle.images[0] || 'https://via.placeholder.com/600x300'}
                              alt=""
                              aria-hidden="true"
                              className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-35"
                            />
                            <div className="absolute inset-0 bg-black/35"></div>
                            <img
                              src={vehicle.images[0] || 'https://via.placeholder.com/600x300'}
                              alt={`${vehicle.make} ${vehicle.model}`}
                              className="relative z-10 w-full h-full object-contain"
                            />
                          </>
                        ) : (
                          <img
                            src={vehicle.images[0] || 'https://via.placeholder.com/600x300'}
                            alt={`${vehicle.make} ${vehicle.model}`}
                            className={`w-full h-full bg-black/30 ${vehicle.make === 'Toyota' && vehicle.model === 'C-HR' ? 'object-cover object-center' : 'object-contain'}`}
                          />
                        )}
                      </div>
                    </button>
                    <div className="absolute top-3 left-3 bg-black/70 text-white px-3 py-2 rounded-md text-sm font-bold border border-red-700/40">
                      ORDER NOW
                    </div>
                    {vehicle.images && vehicle.images.length > 1 && (
                      <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-2 rounded-md text-xs font-bold border border-white/20">
                        {vehicle.images.length} Photos
                      </div>
                    )}
                  </div>

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-white">{vehicle.make} {vehicle.model}</h3>
                        <p className="text-gray-300 text-sm">ETA: {vehicle.eta || '6–8 weeks'}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-red-400">{vehicle.price}</div>
                        <div className="text-gray-300 text-sm">50% Down: {(() => {
                          const priceMatch = vehicle.price.match(/\d+,?\d*/);
                          if (priceMatch) {
                            const price = parseInt(priceMatch[0].replace(',', ''));
                            const downPayment = Math.floor(price / 2);
                            return `KYD $${downPayment.toLocaleString()}`;
                          }
                          return 'Contact for Details';
                        })()}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                      <div>
                        <div className="text-gray-400">Year</div>
                        <div className="text-white font-semibold">{vehicle.year}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Mileage</div>
                        <div className="text-white font-semibold">{vehicle.mileage || '—'}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Engine</div>
                        <div className="text-white font-semibold">{vehicle.engine || '—'}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Transmission</div>
                        <div className="text-white font-semibold">{vehicle.transmission || 'Automatic'}</div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mt-5">
                      <button
                        onClick={() => {
                          setActiveVehicleImageIndex(0);
                          setSelectedVehicleDetails(vehicle);
                        }}
                        className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-3 px-4 rounded-xl font-semibold transition-colors"
                      >
                        View Photos
                      </button>
                      <button
                        onClick={() => scrollToSection('contact')}
                        className="flex-1 bg-red-700 hover:bg-red-600 text-white py-3 px-4 rounded-xl font-semibold transition-colors"
                      >
                        Order Now
                      </button>
                      <button
                        onClick={() => setSelectedVehicle(vehicle)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-xl font-semibold transition-colors"
                      >
                        Reserve
                      </button>
                      <button
                        onClick={() => scrollToSection('contact')}
                        className="flex-1 border-2 border-red-600 text-red-300 hover:bg-red-600 hover:text-white py-3 px-4 rounded-xl font-semibold transition-colors"
                      >
                        Inquire
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Sold Vehicles Section */}
      {soldVehicles.length > 0 && (
        <section id="sold-vehicles" className="py-12 md:py-20 bg-black border-t border-red-900/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-block px-4 py-1 bg-gray-700 text-white rounded-md text-sm font-semibold mb-4">
                Sold Vehicles
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Recently Sold</h2>
              <p className="text-gray-300">Vehicles that are no longer available but show what Gemini Cars Import delivers.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {soldVehicles.map((vehicle) => (
                <div key={vehicle.id} className="bg-gray-900/60 border border-gray-700 rounded-2xl overflow-hidden">
                  <div className="relative">
                    <img
                      src={vehicle.images[0] || 'https://via.placeholder.com/600x300'}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="w-full h-60 object-contain bg-black/30"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 bg-gray-800/90 text-white px-3 py-2 rounded-md text-sm font-bold border border-gray-500/40">
                      SOLD
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-white">{vehicle.make} {vehicle.model} {vehicle.year}</h3>
                        <p className="text-gray-400 text-sm">Previously listed by Gemini Cars Import</p>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-gray-300">{vehicle.price}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                      <div>
                        <div className="text-gray-500">Mileage</div>
                        <div className="text-white font-semibold">{vehicle.mileage || '—'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Engine</div>
                        <div className="text-white font-semibold">{vehicle.engine || '—'}</div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setActiveVehicleImageIndex(0);
                        setSelectedVehicleDetails(vehicle);
                      }}
                      className="mt-5 w-full bg-blue-600 hover:bg-blue-500 text-white py-3 px-4 rounded-xl font-semibold transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Financing Section - Mobile Optimized */}
      <section id="financing" className="py-10 md:py-16 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-black text-white mb-2">Financing Available</h2>
            <p className="text-base md:text-lg text-red-400 font-medium">Easy monthly payments • Competitive rates</p>
          </div>
          
          {/* Payment Calculator - Compact Mobile Design */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-950 border border-red-700/30 rounded-2xl p-4 md:p-6 mb-8 shadow-xl shadow-red-900/10">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-white">Payment Calculator</h3>
            </div>

            <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-6 mb-6">
              <div className="bg-black/30 rounded-xl p-3">
                <label className="block text-gray-400 mb-1 text-xs font-medium">Vehicle Price (KYD)</label>
                <input
                  type="range"
                  min="5000"
                  max="50000"
                  step="500"
                  value={calcPrice}
                  onChange={(e) => setCalcPrice(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
                />
                <div className="text-red-500 font-black text-xl mt-1">${calcPrice.toLocaleString()}</div>
              </div>

              <div className="bg-black/30 rounded-xl p-3">
                <label className="block text-gray-400 mb-1 text-xs font-medium">Down Payment</label>
                <input
                  type="range"
                  min="30"
                  max="70"
                  step="5"
                  value={calcDownPayment}
                  onChange={(e) => setCalcDownPayment(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
                />
                <div className="text-red-500 font-black text-xl mt-1">{calcDownPayment}% <span className="text-gray-400 text-sm font-normal">(${financeQuote.downPaymentAmount.toLocaleString()})</span></div>
              </div>

              <div className="bg-black/30 rounded-xl p-3">
                <label className="block text-gray-400 mb-1 text-xs font-medium">Loan Term</label>
                <div className="flex gap-2 mt-1">
                  {[12, 24, 36].map(months => (
                    <button
                      key={months}
                      onClick={() => setCalcMonths(months)}
                      className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all ${
                        calcMonths === months 
                          ? 'bg-red-600 text-white shadow-lg shadow-red-900/30' 
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {months}mo
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Result Display - Compact */}
            <div className="bg-gradient-to-r from-red-900/30 to-red-800/20 rounded-xl p-4 text-center border border-red-700/30">
              <div className="text-gray-400 text-xs mb-1">Estimated Monthly Payment</div>
              <div className="text-4xl md:text-5xl font-black text-white mb-1">KYD ${financeQuote.monthlyPayment.toLocaleString()}</div>
              <div className="text-red-400 text-sm font-medium">for {calcMonths} months at {financeApr}% APR</div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-left">
                <div className="bg-black/20 rounded-lg p-3">
                  <div className="text-gray-400 text-[10px] uppercase tracking-wide">Amount Financed</div>
                  <div className="text-white font-bold text-base">KYD ${financeQuote.principal.toLocaleString()}</div>
                </div>
                <div className="bg-black/20 rounded-lg p-3">
                  <div className="text-gray-400 text-[10px] uppercase tracking-wide">Total With Finance</div>
                  <div className="text-white font-bold text-base">KYD ${financeQuote.totalWithFinancing.toLocaleString()}</div>
                </div>
                <div className="bg-black/20 rounded-lg p-3 col-span-2">
                  <div className="text-gray-400 text-[10px] uppercase tracking-wide">Finance Charge Above Cash Price</div>
                  <div className="text-green-400 font-bold text-base">KYD ${financeQuote.financeCharge.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <a href="https://wa.me/13459236920?text=Hi%2C%20I%27m%20interested%20in%20financing.%20My%20budget%20is%20around%20KYD%20%24${calcPrice.toLocaleString()}" target="_blank" rel="noopener noreferrer" className="mt-4 w-full bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Apply via WhatsApp
            </a>
            <p className="text-center text-gray-500 text-[10px] mt-2">*Estimate based on {financeApr}% APR. Contact us for exact quotes.</p>
          </div>

          {/* Important Finance Information */}
          <div className="mt-6 bg-gradient-to-r from-gray-900 to-gray-950 border border-yellow-600/30 rounded-xl p-4 md:p-5">
            <div className="flex items-start gap-3">
              <div className="bg-yellow-600/20 p-2 rounded-lg">
                <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-white font-bold text-sm md:text-base mb-2">Important Information</h4>
                <ul className="space-y-2 text-xs md:text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span><strong className="text-white">35 days after down payment</strong> — approval process begins. Once granted, customer will be notified.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span>Depending on the car selected, <strong className="text-white">some vehicles may need to be sourced</strong> if not currently in stock</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span>Contact us via WhatsApp for personalized quotes and availability</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Finance Plans - Compact Cards */}
          <div className="mt-8 md:mt-10">
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-bold text-white">Popular Finance Plans</h3>
            </div>

            {(() => {
              const financePlans = [
                {
                  title: 'Honda Fit 2019',
                  downPayment: 'KYD $5,500',
                  startingFrom: '$350/mo',
                  options: [
                    { monthly: '$350', months: 36 },
                    { monthly: '$450', months: 24 },
                    { monthly: '$650', months: 12 },
                  ],
                },
                {
                  title: 'Toyota CHR 2021',
                  downPayment: 'KYD $9,000',
                  startingFrom: '$399/mo',
                  options: [
                    { monthly: '$399', months: 36 },
                    { monthly: '$550', months: 24 },
                    { monthly: '$750', months: 14 },
                  ],
                },
                {
                  title: 'Honda Grace 2021',
                  downPayment: 'KYD $7,500',
                  startingFrom: '$399/mo',
                  options: [
                    { monthly: '$399', months: 36 },
                    { monthly: '$550', months: 24 },
                    { monthly: '$750', months: 15 },
                  ],
                },
                {
                  title: 'Honda Vezel 2021',
                  downPayment: 'KYD $8,500',
                  startingFrom: '$399/mo',
                  options: [
                    { monthly: '$399', months: 36 },
                    { monthly: '$550', months: 24 },
                    { monthly: '$750', months: 14 },
                  ],
                },
                {
                  title: 'Honda CR-V 2023',
                  downPayment: 'KYD $16,000',
                  startingFrom: '$550/mo',
                  options: [
                    { monthly: '$550', months: 36 },
                    { monthly: '$750', months: 24 },
                    { monthly: '$1,000', months: 12 },
                  ],
                },
              ];

              return (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {financePlans.map((plan) => (
                    <div key={plan.title} className="bg-gradient-to-b from-gray-900 to-gray-950 border border-red-700/30 rounded-xl overflow-hidden">
                      <div className="p-3 md:p-4">
                        <div className="mb-2">
                          <h4 className="text-sm md:text-base font-bold text-white leading-tight">{plan.title}</h4>
                          <div className="text-red-500 font-black text-lg md:text-xl mt-1">From {plan.startingFrom}</div>
                        </div>

                        <div className="bg-black/40 rounded-lg px-2 py-1.5 mb-2">
                          <div className="text-[10px] text-gray-400">Down payment</div>
                          <div className="text-white font-bold text-sm">{plan.downPayment}</div>
                        </div>

                        <div className="space-y-1">
                          {plan.options.map((opt) => (
                            <div key={`${opt.monthly}-${opt.months}`} className="flex items-center justify-between text-xs md:text-sm">
                              <div className="text-white font-medium">{opt.monthly}</div>
                              <div className="text-gray-400">{opt.months}mo</div>
                            </div>
                          ))}
                        </div>

                        <a
                          href={`https://wa.me/13459236920?text=Hi%2C%20I%27m%20interested%20in%20financing%20a%20${encodeURIComponent(plan.title)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 w-full bg-green-600 hover:bg-green-500 text-white py-2 rounded-lg font-semibold text-xs md:text-sm transition-all flex items-center justify-center gap-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          Apply
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Removed: old highlighted Honda Fit finance banner (replaced by modern Finance Plans grid) */}
          {false && (
            <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Side - Starting From */}
            <div className="bg-gray-900/60 p-6 md:p-8 rounded-xl border border-red-700/40">
              <h3 className="text-2xl font-bold text-red-400 mb-4">FINANCING STARTING FROM</h3>
              <div className="text-6xl md:text-7xl font-bold text-white mb-2">$450</div>
              <div className="text-xl text-red-300">/MO.</div>
              
              <div className="mt-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_import-dashboard-3/artifacts/93mpboe7_Screenshot_20260125_090912_WhatsAppBusiness.jpg"
                  alt="Honda Fit"
                  className="w-full h-48 object-contain object-center bg-black/30 rounded-lg"
                />
              </div>
            </div>
            
            {/* Right Side - Featured Vehicle */}
            <div className="bg-gray-900/60 p-6 md:p-8 rounded-xl border border-red-700/40">
              <h3 className="text-2xl font-bold text-white mb-4">2019 HONDA FIT (S PACKAGE) • PENDING ARRIVAL</h3>
              
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-red-300 mb-3">PRICE: KYD $12,500</h4>
                <div className="space-y-2 text-white">
                  <div className="flex justify-between">
                    <span>MILEAGE</span>
                    <span className="text-red-200">89,000 KM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ENGINE</span>
                    <span className="text-red-200">1500cc</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CHASSIS</span>
                    <span className="text-red-200">GP5 (Hybrid)</span>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-red-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-600 transition-colors mb-4"
              >
                COMING SOON (PENDING ARRIVAL)
              </button>
              
              <div className="border-t border-red-700/40 pt-4">
                <h5 className="text-sm font-semibold text-red-300 mb-2">FIND OUT MORE:</h5>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="flex items-center mb-1">
                      <svg className="w-4 h-4 mr-2 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-red-300 font-semibold">CONTACT US:</span>
                    </div>
                    <div className="text-white">
                      <div>+1345-923-6920</div>
                      <div>+1345-923-7665</div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-1">
                      <svg className="w-4 h-4 mr-2 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-red-300 font-semibold">OUR ADDRESS:</span>
                    </div>
                    <div className="text-white">
                      <div>110 NORTH SOUND</div>
                      <div>RD GEORGE TOWN</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
          
          {/* Financing Benefits */}
          <div className="mt-12 bg-gray-900/50 rounded-xl p-6 md:p-8 border border-red-700/30">
            <h3 className="text-2xl md:text-3xl font-bold text-red-400 mb-6 text-center">GEMINI CARS IMPORT PROVIDES</h3>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-4xl mb-3">🔧</div>
                <h4 className="text-lg font-semibold text-white mb-2">1 Year Full Service</h4>
                <p className="text-gray-300 text-sm">Complete maintenance coverage</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">📋</div>
                <h4 className="text-lg font-semibold text-white mb-2">Inspection, Licensing and Full Insurance Coverage</h4>
                <p className="text-red-400 font-bold">($800 CI for every year of financing contract)</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🛡️</div>
                <h4 className="text-lg font-semibold text-white mb-2">3 Month Warranty</h4>
                <p className="text-gray-300 text-sm">Peace of mind guarantee</p>
              </div>
            </div>
            
            <div className="border-t border-gray-700 pt-6">
              <h4 className="text-xl font-bold text-red-400 mb-4">APPROVAL FOR FINANCING</h4>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h5 className="text-lg font-semibold text-white mb-3">1. Proof of a Stable Job:</h5>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Employment letter confirming salary</li>
                    <li>• Copy of valid ID</li>
                    <li>• Work Permit (if applicable)</li>
                  </ul>
                </div>
                <div>

            {/* Warranty Disclaimer */}
            <div className="mt-10 bg-black/40 border border-red-700/40 rounded-xl p-5 md:p-6">
              <h4 className="text-lg font-bold text-red-300 mb-2">Warranty Disclaimer</h4>
              <p className="text-gray-200 text-sm md:text-base leading-relaxed">
                Please note: Our warranty does <span className="font-semibold text-white">not</span> cover electronic parts or normal wear and tear that occur to a vehicle. Some vehicles are brand new, and some are second-hand.
              </p>
            </div>

                  <h5 className="text-lg font-semibold text-white mb-3">2. Self-Employed:</h5>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Source of income (Company Financial Statements for the past year if you own a company or a controlling interest in a company.)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Agent Service Section */}
      <section className="py-10 md:py-14 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="bg-gray-900/60 border border-red-700/40 rounded-2xl p-5 md:p-7">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl md:text-2xl font-bold text-white">Licensing & Inspection Agent Service</h2>
                <p className="mt-2 text-sm md:text-base text-gray-200 leading-relaxed">
                  We provide an agent who can take your vehicle for licensing and inspections for a fee of <span className="font-semibold text-white">KYD $150</span>.
                  This helps you avoid leaving work and waiting for hours.
                </p>
                <p className="mt-2 text-sm md:text-base text-gray-200">
                  If you would like this service, you can request it when you contact us.
                </p>
              </div>

              <div className="w-full sm:w-auto sm:shrink-0 bg-red-700/20 border border-red-700/40 rounded-xl px-4 py-3 text-center">
                <div className="text-xs text-red-200 font-semibold">Service Fee</div>
                <div className="text-lg md:text-xl font-bold text-white">$150</div>
                <div className="text-xs text-gray-300">KYD</div>
              </div>
            </div>

            <button
              onClick={() => scrollToSection('contact')}
              className="mt-5 w-full md:w-auto bg-red-700 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
            >
              Request Agent Service
            </button>
          </div>

          {/* Rate Our Service */}
          <div className="bg-gray-800/70 border border-red-700/30 rounded-2xl p-5 md:p-7">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div className="min-w-0">
                <h3 className="text-xl md:text-2xl font-bold text-white">Rate Our Service</h3>
                <p className="mt-2 text-gray-300 text-sm md:text-base max-w-xl">
                  Tell us how we did. Choose 1-5 stars and optionally leave a short comment.
                </p>

                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <div className="text-sm text-gray-300">Average</div>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }, (_, i) => (
                      <span
                        key={i}
                        className={
                          i < Math.round(ratingSummary.average)
                            ? 'text-yellow-400 text-lg'
                            : 'text-gray-600 text-lg'
                        }
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-300">{ratingSummary.average}/5</div>
                  <div className="text-sm text-gray-400">Trusted by 300+ customers</div>
                </div>
              </div>

              <div className="w-full md:max-w-md">
                {ratingSubmitted ? (
                  <div className="bg-green-700/20 border border-green-600/40 rounded-xl p-4 text-green-200">
                    Thank you! Your rating has been submitted.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, i) => {
                        const value = i + 1;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setRatingStars(value)}
                            className={
                              (value <= ratingStars)
                                ? 'text-yellow-400 text-3xl leading-none'
                                : 'text-gray-600 text-3xl leading-none'
                            }
                            aria-label={`${value} star`}
                          >
                            ★
                          </button>
                        );
                      })}
                    </div>

                    <textarea
                      value={ratingComment}
                      onChange={(e) => setRatingComment(e.target.value)}
                      placeholder="Write a quick note (optional)"
                      className="mt-3 w-full bg-black/30 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm md:text-base"
                      rows={3}
                    />

                    <button
                      type="button"
                      onClick={submitRating}
                      className="mt-3 w-full bg-red-700 hover:bg-red-600 text-white font-semibold py-3 rounded-xl transition-colors"
                    >
                      Submit Rating
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-6 md:py-8 bg-black border-y border-red-900/30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8">
            <div className="flex items-center gap-2 bg-gray-900/50 px-3 py-2 rounded-full border border-red-700/30">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-bold text-xs md:text-sm">Licensed Dealer</div>
                <div className="text-gray-400 text-[10px] md:text-xs">Cayman Islands</div>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-gray-900/50 px-4 py-2 rounded-full border border-red-700/30">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-bold text-sm">10+ Years</div>
                <div className="text-gray-400 text-xs">Experience</div>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-gray-900/50 px-4 py-2 rounded-full border border-red-700/30">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-bold text-sm">3-Month Warranty</div>
                <div className="text-gray-400 text-xs">On All Vehicles</div>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-gray-900/50 px-4 py-2 rounded-full border border-red-700/30">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-bold text-sm">150+ Happy</div>
                <div className="text-gray-400 text-xs">Customers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose Gemini Cars Import?</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">We&apos;ve been importing quality vehicles for over 10 years. Here&apos;s what sets us apart.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800/50 border border-red-700/30 rounded-2xl p-6 text-center hover:border-red-500/50 transition-colors">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Global Sourcing</h3>
              <p className="text-gray-400 text-sm">Quality vehicles from Japan, UK, and USA auctions</p>
            </div>

            <div className="bg-gray-800/50 border border-red-700/30 rounded-2xl p-6 text-center hover:border-red-500/50 transition-colors">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Easy Financing</h3>
              <p className="text-gray-400 text-sm">Flexible payment plans starting from KYD $350/month</p>
            </div>

            <div className="bg-gray-800/50 border border-red-700/30 rounded-2xl p-6 text-center hover:border-red-500/50 transition-colors">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Full Warranty</h3>
              <p className="text-gray-400 text-sm">3-month warranty + 1 year free inspections</p>
            </div>

            <div className="bg-gray-800/50 border border-red-700/30 rounded-2xl p-6 text-center hover:border-red-500/50 transition-colors">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Fast Delivery</h3>
              <p className="text-gray-400 text-sm">6-8 weeks from order to your doorstep</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-gray-300 text-lg">Simple 4-step process to get your dream car</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-red-600 via-red-500 to-red-600"></div>

            <div className="relative text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold text-white relative z-10 border-4 border-black">
                1
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Choose Your Car</h3>
              <p className="text-gray-400 text-sm">Browse our inventory or tell us what you want. We&apos;ll find it!</p>
            </div>

            <div className="relative text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold text-white relative z-10 border-4 border-black">
                2
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Pay 50% Down</h3>
              <p className="text-gray-400 text-sm">Secure your vehicle with a 50% down payment</p>
            </div>

            <div className="relative text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold text-white relative z-10 border-4 border-black">
                3
              </div>
              <h3 className="text-xl font-bold text-white mb-2">We Import</h3>
              <p className="text-gray-400 text-sm">We handle shipping, customs, and all paperwork</p>
            </div>

            <div className="relative text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold text-white relative z-10 border-4 border-black">
                4
              </div>
              <h3 className="text-xl font-bold text-white mb-2">You Drive!</h3>
              <p className="text-gray-400 text-sm">Pay remaining 50%, get your keys, and enjoy!</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-colors"
            >
              Start Your Order Today
            </button>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section id="testimonials" className="py-12 md:py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Happy Customers</h2>
            <p className="text-lg md:text-xl text-gray-300">See what our customers say about their imported vehicles</p>
          </div>
          
          <div className="space-y-6 md:space-y-8">
            {displayCustomers.map((customer) => (
              <div key={customer.id} className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700/50">
                <div className="p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-2">
                    <div>
                      <h3 className="text-lg md:text-xl font-bold text-white">{customer.name}</h3>
                      <p className="text-gray-400 text-sm md:text-base">{customer.car} {customer.year}</p>
                    </div>
                    <div className="text-gray-400 text-sm">
                      {customer.sold_year ? `Sold in ${customer.sold_year}` : formatCustomerDate(customer.created_at)}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    {(() => {
                      // Use object-contain for recent customers to show full image without cutting
                      const isRecent2026 = customer?.sold_year === 2026;
                      return (
                        <img 
                          src={customer.image} 
                          alt={customer.name} 
                          className={
                            isRecent2026
                              ? 'w-full h-80 md:h-96 object-contain bg-gray-900 rounded-lg'
                              : 'w-full h-80 md:h-96 object-cover object-center rounded-lg'
                          }
                        />
                      );
                    })()}
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center gap-1 mb-3">
                      {renderStars(5)}
                    </div>
                  </div>
                  
                  <blockquote className="text-gray-300 italic text-base md:text-lg leading-relaxed">
                    &quot;{customer.testimonial || customer.review || 'Satisfied with Gemini Cars quality and service!'}&quot;
                  </blockquote>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-300 text-lg">Got questions? We&apos;ve got answers.</p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="bg-gray-800/50 border border-red-700/30 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="text-white font-semibold text-lg pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`}>
                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-gray-400 mb-4">Still have questions?</p>
            <button 
              onClick={() => scrollToSection('contact')}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Social Media / Follow Us Section */}
      <section className="py-16 md:py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Follow Us</h2>
            <p className="text-gray-300 text-lg">Stay updated with our latest imports and deals</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Facebook */}
            <a 
              href="https://www.facebook.com/geminicarsimport" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-gray-900/60 border border-red-700/30 rounded-2xl p-6 hover:border-red-500/50 transition-all group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white group-hover:text-red-400 transition-colors">Facebook</h3>
                  <p className="text-gray-400">@geminicarsimport</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">See our latest vehicle arrivals, customer photos, and special promotions!</p>
              <div className="mt-4 text-red-400 font-semibold text-sm group-hover:text-red-300">
                Follow us on Facebook →
              </div>
            </a>

            {/* Instagram */}
            <a 
              href="https://www.instagram.com/geminicarsimport" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-gray-900/60 border border-red-700/30 rounded-2xl p-6 hover:border-red-500/50 transition-all group"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white group-hover:text-red-400 transition-colors">Instagram</h3>
                  <p className="text-gray-400">@geminicarsimport</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">Check out our vehicle gallery and behind-the-scenes of our imports!</p>
              <div className="mt-4 text-red-400 font-semibold text-sm group-hover:text-red-300">
                Follow us on Instagram →
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Get in Touch</h2>
              <p className="text-xl text-gray-300 mb-8">Ready to import your dream car? Contact us today!</p>
              
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="bg-red-600 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Direct</h3>
                    <a href="tel:+13459306988" className="text-red-400 hover:text-red-300">+1 (345) 930-6988</a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-green-600 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">WhatsApp</h3>
                    <div className="space-y-1">
                      <a href="https://wa.me/13459236920" className="text-green-400 hover:text-green-300 block">+1 (345) 923-6920</a>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-blue-600 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Email</h3>
                    <a href="mailto:Geminicarsimport@gmail.com" className="text-blue-400 hover:text-blue-300">Geminicarsimport@gmail.com</a>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="mt-8 bg-gray-900 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-white mb-4">Business Hours</h3>
                <div className="space-y-2 text-gray-300">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="text-red-400">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="text-red-400">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="text-red-400">Closed</span>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="mt-8 bg-gray-900 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-white mb-4">Location</h3>
                <p className="text-gray-300 mb-2">110 North Sound Road</p>
                <p className="text-gray-300 mb-4">Grand Cayman Islands</p>
                <p className="text-gray-300">
                  <span className="text-red-400 font-semibold">Import From:</span><br />
                  Japan | UK | USA
                </p>
              </div>
            </div>
            
            <div className="bg-gray-900 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Send a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Name *</label>
                  <input 
                    type="text" 
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Email *</label>
                  <input 
                    type="email" 
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Phone</label>
                  <input 
                    type="tel" 
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Vehicle Interest</label>
                  <select 
                    value={contactForm.vehicle_interest}
                    onChange={(e) => setContactForm({...contactForm, vehicle_interest: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none"
                  >
                    <option value="">Select a vehicle type</option>
                    <option value="Toyota Aqua">Toyota Aqua</option>
                    <option value="Honda Fit">Honda Fit</option>
                    <option value="Toyota CHR">Toyota CHR</option>
                    <option value="Honda CRV">Honda CRV</option>
                    <option value="BMW">BMW</option>
                    <option value="Mercedes Benz">Mercedes Benz</option>
                    <option value="Nissan Note">Nissan Note</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Message *</label>
                  <textarea 
                    rows="4" 
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none"
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-md font-semibold transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_car-showcase-105/artifacts/805j4p0n_Gemini%20Car%20Imports%20%28Logo%29%202%202021.png" 
                  alt="Gemini Cars Import Logo" 
                  className="h-10 w-10 rounded-full mr-3"
                />
                <span className="text-xl font-bold text-white">Gemini Cars Import</span>
              </div>
              <p className="text-gray-400 mb-4">Premium imported vehicles from Japan, UK, and USA. Your trusted car import specialists in Grand Cayman Islands.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-white">Home</button></li>
                <li><button onClick={() => scrollToSection('inventory')} className="hover:text-white">Inventory</button></li>
                <li><button onClick={() => scrollToSection('order-now')} className="hover:text-white">Order Now</button></li>
                <li><button onClick={() => scrollToSection('financing')} className="hover:text-white">Financing</button></li>
                <li><a href="/imports" className="hover:text-white">Imports</a></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="hover:text-white">Testimonials</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Popular Models</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Toyota Aqua</li>
                <li>Honda Fit</li>
                <li>Toyota CHR</li>
                <li>Honda CRV</li>
                <li>BMW</li>
                <li>Mercedes Benz</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact Info</h3>
              <div className="space-y-2 text-gray-400">
                <p>110 North Sound Road</p>
                <p>Grand Cayman Islands</p>
                <a href="tel:+13459306988" className="block hover:text-white">+1 (345) 930-6988</a>
                <a href="mailto:Geminicarsimport@gmail.com" className="block hover:text-white">Geminicarsimport@gmail.com</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2026 Gemini Cars Import. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Vehicle Details Modal (legacy - disabled to avoid duplicate modals) */}
      {false && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 md:p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-white">Vehicle Details</h3>
                <button 
                  onClick={() => setSelectedVehicleDetails(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Vehicle Image */}
              <div className="mb-6">
                <img 
                  src={selectedVehicleDetails.images[0]} 
                  alt={`${selectedVehicleDetails.make} ${selectedVehicleDetails.model}`}
                  className="w-full h-48 md:h-64 object-contain object-center bg-gray-700 rounded-lg"
                />
              </div>
              
              {/* Vehicle Title */}
              <div className="mb-6">
                <h4 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {selectedVehicleDetails.make} {selectedVehicleDetails.model} {selectedVehicleDetails.year}
                </h4>
                <p className="text-gray-400">{selectedVehicleDetails.color} • {selectedVehicleDetails.trim}</p>
              </div>
              
              {/* Vehicle Specifications */}
              <div className="mb-6">
                <h5 className="text-lg font-semibold text-white mb-4">Specifications</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-gray-400 text-sm">Status</div>
                    <div className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(selectedVehicleDetails.status).bg} text-white`}>
                      {getStatusBadge(selectedVehicleDetails.status).text}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Year</div>
                    <div className="text-white">{selectedVehicleDetails.year}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Mileage</div>
                    <div className="text-white">{selectedVehicleDetails.mileage || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Transmission</div>
                    <div className="text-white">{selectedVehicleDetails.transmission || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Engine</div>
                    <div className="text-white">{selectedVehicleDetails.engine || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">Color</div>
                    <div className="text-white">{selectedVehicleDetails.color}</div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm">From</div>
                    <div className="text-white">{selectedVehicleDetails.from_country || 'Japan'}</div>
                  </div>
                  {selectedVehicleDetails.chassis && (
                    <div>
                      <div className="text-gray-400 text-sm">Chassis</div>
                      <div className="text-white">{selectedVehicleDetails.chassis}</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Features/Specs - Always Show */}
              <div className="mb-6">
                <h5 className="text-lg font-semibold text-white mb-3">🚗 Features & Equipment</h5>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {selectedVehicleDetails.specs || 'PS | PW | ABS | AB | TV | AC | BLUETOOTH | NAVIGATION | BACK CAMERA | KEYLESS ENTRY'}
                </p>
              </div>
              
              {/* Pricing */}
              <div className="mb-6">
                <div className="text-2xl md:text-3xl font-bold text-red-400 mb-2">{selectedVehicleDetails.price}</div>
                {selectedVehicleDetails.price !== 'Contact for Price' && (
                  <div className="text-gray-300">50% Down: {
                    (() => {
                      const priceMatch = selectedVehicleDetails.price.match(/\\d+,?\\d*/);
                      if (priceMatch) {
                        const price = parseInt(priceMatch[0].replace(',', ''));
                        const downPayment = Math.floor(price / 2);
                        return `KYD $${downPayment.toLocaleString()}`;
                      }
                      return 'Contact for Details';
                    })()
                  }</div>
                )}
              </div>

              {/* Warranty Information - ALWAYS VISIBLE */}
              <div className="mb-8 bg-green-600 border-4 border-green-400 rounded-xl p-6 shadow-2xl">
                <div className="text-center mb-4">
                  <h5 className="text-2xl font-bold text-white mb-2">
                    🛡️ WARRANTY & BENEFITS 🛡️
                  </h5>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-center text-white text-lg font-bold bg-green-700 rounded-lg p-3">
                    <span className="text-2xl mr-3">✅</span>
                    <span>3 MONTHS WARRANTY INCLUDED</span>
                  </div>
                  <div className="flex items-center justify-center text-white text-lg font-bold bg-green-700 rounded-lg p-3">
                    <span className="text-2xl mr-3">✅</span>
                    <span>1 YEAR FREE INSPECTIONS</span>
                  </div>
                  <div className="text-center text-white font-semibold bg-green-800 rounded-lg p-2 text-sm">
                    Peace of mind with every purchase from Gemini Cars Import!
                  </div>
                </div>
              </div>
              
              {/* Description */}
              {selectedVehicleDetails.description && (
                <div className="mb-6">
                  <h5 className="text-lg font-semibold text-white mb-3">Description</h5>
                  <p className="text-gray-300 leading-relaxed">
                    {selectedVehicleDetails.description}
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button 
                  type="button"
                  onClick={() => setSelectedVehicleDetails(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-md font-semibold transition-colors"
                >
                  Close
                </button>
                
                {canReserve(selectedVehicleDetails.status) ? (
                  <button 
                    onClick={() => {
                      setSelectedVehicle(selectedVehicleDetails);
                      setSelectedVehicleDetails(null);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Reserve Now
                  </button>
                ) : (
                  <button 
                    onClick={() => {
                      setSelectedVehicleDetails(null);
                      scrollToSection('contact');
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Contact Us
                  </button>
                )}
                
                <button 
                  onClick={() => {
                    setSelectedVehicleDetails(null);
                    scrollToSection('contact');
                  }}
                  className="flex-1 border-2 border-red-600 text-red-400 hover:bg-red-600 hover:text-white py-3 rounded-md font-semibold transition-colors"
                >
                  Inquire
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Modal */}
      {selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Reserve Vehicle</h3>
                <button 
                  onClick={() => setSelectedVehicle(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-6">
                <img 
                  src={selectedVehicle.images[0]} 
                  alt={`${selectedVehicle.make} ${selectedVehicle.model}`}
                  className="w-full h-48 object-cover rounded-lg mb-4"
                />
                <h4 className="text-lg font-semibold text-white">
                  {selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year}
                </h4>
                <p className="text-gray-400">{selectedVehicle.color} • {selectedVehicle.price}</p>
                <p className="text-red-400 font-semibold mt-2">50% Down Payment Required to Reserve</p>
              </div>
              
              <form onSubmit={handleReservation} className="space-y-4">
                <div>
                  <label className="block text-gray-300 mb-2">Your Name *</label>
                  <input 
                    type="text" 
                    required
                    value={reservationForm.customer_name}
                    onChange={(e) => setReservationForm({...reservationForm, customer_name: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Email *</label>
                  <input 
                    type="email" 
                    required
                    value={reservationForm.customer_email}
                    onChange={(e) => setReservationForm({...reservationForm, customer_email: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Phone *</label>
                  <input 
                    type="tel" 
                    required
                    value={reservationForm.customer_phone}
                    onChange={(e) => setReservationForm({...reservationForm, customer_phone: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Down Payment Amount *</label>
                  <input 
                    type="text" 
                    required
                    placeholder="e.g., KYD $4,875 (50%)"
                    value={reservationForm.down_payment}
                    onChange={(e) => setReservationForm({...reservationForm, down_payment: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-md border border-gray-700 focus:border-red-500 focus:outline-none" 
                  />
                </div>
                <div className="flex space-x-4">
                  <button 
                    type="button"
                    onClick={() => setSelectedVehicle(null)}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Submit Reservation
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Details Modal */}
      {selectedVehicleDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">Vehicle Details</h3>
                <button 
                  onClick={() => setSelectedVehicleDetails(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Vehicle Images */}
              <div className="mb-6">
                {selectedVehicleDetails.images && selectedVehicleDetails.images.length > 0 ? (
                  <div>
                    <div className="relative overflow-hidden rounded-xl bg-black/30 border border-red-700/20">
                      <img 
                        src={selectedVehicleDetails.images[activeVehicleImageIndex]}
                        alt={`${selectedVehicleDetails.make} ${selectedVehicleDetails.model} - Image ${activeVehicleImageIndex + 1}`}
                        className="w-full h-72 md:h-96 object-cover"
                        loading="lazy"
                      />
                      <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-3 py-1 rounded-full">
                        {activeVehicleImageIndex + 1} / {selectedVehicleDetails.images.length}
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-3">
                      {selectedVehicleDetails.images.map((image, index) => (
                        <button
                          key={image}
                          onClick={() => setActiveVehicleImageIndex(index)}
                          className={`rounded-lg overflow-hidden border transition-all ${
                            activeVehicleImageIndex === index ? 'border-red-500 ring-2 ring-red-500/40' : 'border-gray-700 hover:border-red-500/50'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`${selectedVehicleDetails.make} ${selectedVehicleDetails.model} thumbnail ${index + 1}`}
                            className="w-full h-20 object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <img 
                    src="https://via.placeholder.com/600x300" 
                    alt={`${selectedVehicleDetails.make} ${selectedVehicleDetails.model}`}
                    className="w-full h-72 md:h-96 object-cover rounded-xl"
                  />
                )}
              </div>
              
              {/* Vehicle Information */}

              {/* Warranty & Benefits (always visible) */}
              <div className="mb-6 bg-green-700/30 border border-green-500 rounded-xl p-4 md:p-5">
                <h5 className="text-lg font-bold text-green-300 mb-3">Warranty & Benefits</h5>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="bg-green-700/40 rounded-lg p-3 text-white font-semibold">
                    ✅ 3 Month Warranty
                  </div>
                  <div className="bg-green-700/40 rounded-lg p-3 text-white font-semibold">
                    ✅ 1 Year Free Inspection
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-xl font-bold text-white mb-4">
                    {selectedVehicleDetails.make} {selectedVehicleDetails.model} {selectedVehicleDetails.year}
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status:</span>
                      <span className={`px-2 py-1 rounded text-sm font-semibold ${getStatusBadge(selectedVehicleDetails.status).bg} text-white`}>
                        {getStatusBadge(selectedVehicleDetails.status).text}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Year:</span>
                      <span className="text-white">{selectedVehicleDetails.year}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Mileage:</span>
                      <span className="text-white">{selectedVehicleDetails.mileage || '30,144 km'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Transmission:</span>
                      <span className="text-white">{selectedVehicleDetails.transmission || 'Automatic'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Engine:</span>
                      <span className="text-white">{selectedVehicleDetails.engine || 'Hybrid'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Color:</span>
                      <span className="text-white">{selectedVehicleDetails.color || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">From Country:</span>
                      <span className="text-white">{getCountryFlag(selectedVehicleDetails.from_country || 'Japan')} {selectedVehicleDetails.from_country || 'Japan'}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h5 className="text-lg font-semibold text-white mb-4">Pricing</h5>
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <div className="text-3xl font-bold text-red-400 mb-2">{selectedVehicleDetails.price}</div>
                    <div className="text-gray-300 mb-4">50% Down Payment: {
                      (() => {
                        const priceMatch = selectedVehicleDetails.price.match(/\d+,?\d*/);
                        if (priceMatch) {
                          const price = parseInt(priceMatch[0].replace(',', ''));
                          const downPayment = Math.floor(price / 2);
                          return `KYD $${downPayment.toLocaleString()}`;
                        }
                        return 'Contact for Details';
                      })()
                    }</div>
                    <p className="text-sm text-gray-400">
                      Pay 50% down payment to reserve this vehicle for 30 days while we import it for you.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              {selectedVehicleDetails.description && (
                <div className="mb-6">
                  <h5 className="text-lg font-semibold text-white mb-3">Description</h5>
                  <p className="text-gray-300 leading-relaxed">
                    {selectedVehicleDetails.description}
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setSelectedVehicleDetails(null)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-md font-semibold transition-colors"
                >
                  Close
                </button>
                
                {canReserve(selectedVehicleDetails.status) ? (
                  <button 
                    onClick={() => {
                      setSelectedVehicle(selectedVehicleDetails);
                      setSelectedVehicleDetails(null);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Reserve This Vehicle
                  </button>
                ) : selectedVehicleDetails.status === 'SOLD' ? (
                  <div className="flex-1 bg-gray-700 text-gray-400 py-3 rounded-md text-center font-semibold">
                    SOLD
                  </div>
                ) : (
                  <button 
                    onClick={() => {
                      setSelectedVehicleDetails(null);
                      scrollToSection('contact');
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition-colors"
                  >
                    Contact for Details
                  </button>
                )}
                
                <button 
                  onClick={() => {
                    setSelectedVehicleDetails(null);
                    scrollToSection('contact');
                  }}
                  className="flex-1 border-2 border-red-600 text-red-400 hover:bg-red-600 hover:text-white py-3 rounded-md font-semibold transition-colors"
                >
                  Inquire About This Vehicle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Floating Button - Enhanced for Mobile */}
      <a
        href="https://wa.me/13459236920"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 md:bottom-24 right-4 md:right-6 z-50 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white p-3 md:p-4 rounded-full shadow-xl shadow-green-900/40 hover:shadow-2xl transition-all hover:scale-110 group animate-bounce"
        style={{ animationDuration: '2s', animationIterationCount: '3' }}
        aria-label="Chat on WhatsApp"
      >
        <svg className="w-6 h-6 md:w-7 md:h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
        <span className="hidden md:block absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity font-medium">
          Message us!
        </span>
      </a>
    </div>
  );
}

export default App;